import type { FunctionComponent, MouseEvent } from 'react';

import { Gtm } from '@/core/tracking/utils/Gtm';
import { useCategoryFamilyId } from '@/domains/productDiscovery/Listings/pages/Category/hooks/useCategoryFamilyId';
import { useCategoryId } from '@/domains/productDiscovery/Listings/pages/Category/hooks/useCategoryId';
import { useCategoryLevel } from '@/domains/productDiscovery/Listings/pages/Category/hooks/useCategoryLevel';
import { useFacet } from '@/productDiscovery/Listings/facets/hooks/useFacet';

import { FilterBlock } from '../FilterBlock/FilterBlock';
import type { Item } from '../MultiSelect/interfaces';
import { MultiSelect } from '../MultiSelect/MultiSelect';
import { generateRatingItems } from './utils/generateRatingItems';

import styles from './RatingsFilter.module.scss';

interface Props {
  className?: string;
  title: string;
  facetName: string;
  position?: number;
  filterCount?: number;
}

export const RatingsFilter: FunctionComponent<Props> = ({
  className,
  title,
  facetName,
  position,
  filterCount,
}) => {
  const categoryId = useCategoryId();
  const categoryLevel = useCategoryLevel();
  const categoryFamilyId = useCategoryFamilyId();

  const { facet, refineFacetValue } = useFacet(facetName);

  const handleChange = async (
    event: MouseEvent<HTMLInputElement>,
    selectedFacet: Item,
  ) => {
    refineFacetValue(
      selectedFacet.isRefined ? undefined : selectedFacet.value,
      true,
    );
    const { generateFiltersTracking } = await import(
      '../../tracking/generateFiltersTracking'
    );

    Gtm.push(
      generateFiltersTracking({
        e_label: selectedFacet.value,
        filter_id: facetName,
        filter_name: title,
        filter_value: selectedFacet.value,
        filter_position: position,
        filter_count: filterCount,
        category_id: categoryId,
        category_level: categoryLevel,
        family_id: categoryFamilyId,
      }),
    );
  };

  const hasValues = (facet?.values || []).length > 0;
  return hasValues ? (
    <FilterBlock
      className={className}
      id={facetName}
      title={title}
      disabled={!hasValues}
    >
      <MultiSelect
        className={styles.multiSelect}
        items={generateRatingItems(facet?.values)}
        onChange={handleChange}
      />
    </FilterBlock>
  ) : null;
};

RatingsFilter.displayName = 'RatingsFilter';
